import { Chip, Description, Label, Skeleton, Spinner } from "@heroui/react";
import { CheckboxButtonGroup } from "@heroui-pro/react/checkbox-button-group";
import { RadioButtonGroup } from "@heroui-pro/react/radio-button-group";
import { memo, useState, type ReactNode } from "react";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

export type ChoiceCardBadge = {
  label: string;
  tone?: "default" | "accent" | "warning" | "success";
};

type ChoiceCardProps = {
  /** Required. Identifier the parent group uses to track selection. */
  value: string;
  /** Selects which Pro group primitive to render under the hood. Must match the parent group. */
  role: "radio" | "checkbox";
  label: string;
  description?: string;
  ariaLabel?: string;
  imageUrl?: string;
  imageAlt?: string;
  /** CSS `object-position` for cover images (e.g. "top", "center"). Defaults to "center". */
  imagePosition?: string;
  /** Optional image aspect ratio for tile and cover layouts. */
  imageAspect?: "square" | "portrait" | "landscape";
  iconLeading?: ReactNode;
  badge?: ChoiceCardBadge;
  /** `row` (default) shows label + description with indicator on the right; `tile` shows image-on-top; `cover` shows image-as-background with text overlay. */
  layout?: "row" | "tile" | "cover";
  disabled?: boolean;
  /** When false, hides the radio/checkbox indicator on row layout. Defaults to true. */
  showIndicator?: boolean;
  /** Replaces the selection indicator with a Spinner. Blocks interaction. */
  isLoading?: boolean;
  /** Index within the parent options array; enables inline editing paths like `options[0].label`. */
  optionIndex?: number;
  className?: string;
  testId?: string;
};

const IMAGE_ASPECT_CLASSES = {
  square: "aspect-square",
  portrait: "aspect-[3/4]",
  landscape: "aspect-[4/3]",
} as const;

type ContentProps = Pick<
  ChoiceCardProps,
  "label" | "description" | "iconLeading" | "badge" | "optionIndex"
>;

const RowContent = ({
  label,
  description,
  iconLeading,
  badge,
  optionIndex,
  IconSlot,
}: ContentProps & { IconSlot: ReactNode }) => (
  <>
    {iconLeading && IconSlot}
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <Label
          {...(optionIndex !== undefined
            ? { "data-editable-path": `options[${optionIndex}].label` }
            : {})}
        >
          {label}
        </Label>
        {badge && (
          <Chip color={badge.tone ?? "default"} size="sm" variant="soft">
            {badge.label}
          </Chip>
        )}
      </div>
      {description && (
        <Description
          {...(optionIndex !== undefined
            ? { "data-editable-path": `options[${optionIndex}].description` }
            : {})}
        >
          {description}
        </Description>
      )}
    </div>
  </>
);

const TileImage = ({
  imageUrl,
  imageAlt,
  imagePosition,
  imageAspect,
  badge,
}: Pick<ChoiceCardProps, "imageUrl" | "imageAlt" | "imagePosition" | "imageAspect" | "badge">) => {
  const [loaded, setLoaded] = useState(false);
  if (!imageUrl) return null;
  return (
    <div className="relative w-full overflow-hidden rounded-lg">
      {!loaded && <Skeleton aria-hidden="true" className="absolute inset-0 z-0 h-full w-full" />}
      <ResponsiveAsset
        src={imageUrl}
        alt={imageAlt ?? ""}
        loading="eager"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`relative z-10 ${imageAspect ? IMAGE_ASPECT_CLASSES[imageAspect] : "aspect-[4/3]"} w-full object-cover transition-opacity duration-200`}
        style={{ opacity: loaded ? 1 : 0.001, objectPosition: imagePosition ?? "center" }}
      />
      {badge && (
        <span className="absolute left-2 top-2 z-20">
          <Chip color={badge.tone ?? "default"} size="sm" variant="soft">
            {badge.label}
          </Chip>
        </span>
      )}
    </div>
  );
};

const TileContent = ({
  label,
  description,
  imageUrl,
  imageAlt,
  imagePosition,
  imageAspect,
  iconLeading,
  badge,
  optionIndex,
}: Pick<
  ChoiceCardProps,
  | "label"
  | "description"
  | "imageUrl"
  | "imageAlt"
  | "imagePosition"
  | "imageAspect"
  | "iconLeading"
  | "badge"
  | "optionIndex"
>) => {
  if (iconLeading && !imageUrl) {
    return (
      <div className="flex w-full flex-col items-start gap-3.5 text-left">
        <span
          aria-hidden="true"
          className="flex h-11 w-11 items-center justify-center rounded-xl"
          style={{ background: "hsl(var(--brand-bg-foreground) / 0.06)" }}
          {...(optionIndex !== undefined
            ? { "data-editable-path": `options[${optionIndex}].iconLeading` }
            : {})}
        >
          {iconLeading}
        </span>
        <div className="flex flex-col gap-1">
          <Label
            className="text-sm font-bold"
            {...(optionIndex !== undefined
              ? { "data-editable-path": `options[${optionIndex}].label` }
              : {})}
          >
            {label}
          </Label>
          {description && (
            <Description
              {...(optionIndex !== undefined
                ? { "data-editable-path": `options[${optionIndex}].description` }
                : {})}
            >
              {description}
            </Description>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col items-center gap-2 text-center">
      {imageUrl ? (
        <TileImage
          imageUrl={imageUrl}
          imageAlt={imageAlt}
          imagePosition={imagePosition}
          imageAspect={imageAspect}
          badge={badge}
        />
      ) : null}
      <div className="flex flex-col items-center gap-0.5 px-1 pb-1">
        <Label
          {...(optionIndex !== undefined
            ? { "data-editable-path": `options[${optionIndex}].label` }
            : {})}
        >
          {label}
        </Label>
        {description && (
          <Description
            {...(optionIndex !== undefined
              ? { "data-editable-path": `options[${optionIndex}].description` }
              : {})}
          >
            {description}
          </Description>
        )}
      </div>
    </div>
  );
};

const HOVER_CLASSES_ROW =
  "transition-[border-color,background-color,transform] duration-150 hover:border-[hsl(var(--brand-primary)/0.5)] hover:bg-[hsl(var(--brand-bg-foreground)/0.02)]";

const HOVER_CLASSES_TILE =
  "transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-[hsl(var(--brand-primary)/0.5)] hover:shadow-[0_8px_24px_-12px_hsl(var(--brand-bg-foreground)/0.25)]";

type GroupBindings = {
  Item: typeof RadioButtonGroup.Item | typeof CheckboxButtonGroup.Item;
  ItemContent: typeof RadioButtonGroup.ItemContent | typeof CheckboxButtonGroup.ItemContent;
  ItemIcon: typeof RadioButtonGroup.ItemIcon | typeof CheckboxButtonGroup.ItemIcon;
  Indicator: typeof RadioButtonGroup.Indicator | typeof CheckboxButtonGroup.Indicator;
};

const radioBindings: GroupBindings = {
  Item: RadioButtonGroup.Item,
  ItemContent: RadioButtonGroup.ItemContent,
  ItemIcon: RadioButtonGroup.ItemIcon,
  Indicator: RadioButtonGroup.Indicator,
};

const checkboxBindings: GroupBindings = {
  Item: CheckboxButtonGroup.Item,
  ItemContent: CheckboxButtonGroup.ItemContent,
  ItemIcon: CheckboxButtonGroup.ItemIcon,
  Indicator: CheckboxButtonGroup.Indicator,
};

const CoverContent = ({
  label,
  description,
  imageUrl,
  imageAlt,
  imagePosition,
  imageAspect,
  badge,
  optionIndex,
}: Pick<
  ChoiceCardProps,
  | "label"
  | "description"
  | "imageUrl"
  | "imageAlt"
  | "imagePosition"
  | "imageAspect"
  | "badge"
  | "optionIndex"
>) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      className={`relative flex w-full flex-col overflow-hidden rounded-xl ${imageAspect ? IMAGE_ASPECT_CLASSES[imageAspect] : ""}`}
      style={imageAspect ? undefined : { minHeight: 180 }}
    >
      {imageUrl && (
        <>
          {!loaded && (
            <Skeleton aria-hidden="true" className="absolute inset-0 z-0 h-full w-full" />
          )}
          <ResponsiveAsset
            src={imageUrl}
            alt={imageAlt ?? ""}
            loading="eager"
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            className="absolute inset-0 z-0 h-full w-full object-cover transition-opacity duration-200"
            style={{ opacity: loaded ? 1 : 0.001, objectPosition: imagePosition ?? "center" }}
          />
        </>
      )}
      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.08) 65%, transparent 100%), linear-gradient(135deg, transparent 40%, rgba(0,0,0,0.35) 100%)",
        }}
      />
      <div className="relative z-20 mt-auto flex flex-col gap-1 p-4">
        {badge && (
          <span className="mb-1">
            <Chip color={badge.tone ?? "default"} size="sm" variant="soft">
              {badge.label}
            </Chip>
          </span>
        )}
        <Label
          className="text-sm font-bold text-white"
          {...(optionIndex !== undefined
            ? { "data-editable-path": `options[${optionIndex}].label` }
            : {})}
        >
          {label}
        </Label>
        {description && (
          <Description
            className="text-white/70"
            {...(optionIndex !== undefined
              ? { "data-editable-path": `options[${optionIndex}].description` }
              : {})}
          >
            {description}
          </Description>
        )}
      </div>
    </div>
  );
};

const HOVER_CLASSES_COVER =
  "transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-[hsl(var(--brand-primary)/0.5)] hover:shadow-[0_8px_24px_-12px_hsl(var(--brand-bg-foreground)/0.25)]";

const ChoiceCardImpl = ({
  value,
  role,
  label,
  description,
  ariaLabel,
  imageUrl,
  imageAlt,
  imagePosition,
  imageAspect,
  iconLeading,
  badge,
  layout = "row",
  showIndicator = true,
  disabled,
  isLoading,
  optionIndex,
  className,
  testId,
}: ChoiceCardProps) => {
  const isTile = layout === "tile";
  const isCover = layout === "cover";
  const blockInteraction = disabled || isLoading;
  const bindings = role === "radio" ? radioBindings : checkboxBindings;
  const { Item, ItemContent, ItemIcon, Indicator } = bindings;
  const indicator = isLoading ? (
    <span aria-hidden="true" className="absolute right-4 top-3 inline-flex">
      <Spinner size="sm" />
    </span>
  ) : (
    <Indicator />
  );

  const hoverClasses = blockInteraction
    ? ""
    : isCover
      ? HOVER_CLASSES_COVER
      : isTile
        ? HOVER_CLASSES_TILE
        : HOVER_CLASSES_ROW;
  const itemClasses = [
    isTile ? "flex-col p-3" : null,
    isCover ? "overflow-hidden p-0" : null,
    hoverClasses,
    className ?? null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Item
        value={value}
        isDisabled={blockInteraction}
        aria-label={ariaLabel}
        data-testid={testId}
        data-loading={isLoading || undefined}
        className={itemClasses}
      >
        {isCover ? (
          <ItemContent className="flex-col gap-0 p-0">
            <CoverContent
              label={label}
              description={description}
              imageUrl={imageUrl}
              imageAlt={imageAlt}
              imagePosition={imagePosition}
              imageAspect={imageAspect}
              badge={badge}
              optionIndex={optionIndex}
            />
          </ItemContent>
        ) : isTile ? (
          <ItemContent className="flex-col gap-2 p-0">
            <TileContent
              label={label}
              description={description}
              imageUrl={imageUrl}
              imageAlt={imageAlt}
              imagePosition={imagePosition}
              imageAspect={imageAspect}
              iconLeading={iconLeading}
              badge={badge}
              optionIndex={optionIndex}
            />
          </ItemContent>
        ) : (
          <>
            <ItemContent className="flex-row items-center gap-3">
              <RowContent
                label={label}
                description={description}
                iconLeading={iconLeading}
                badge={badge}
                optionIndex={optionIndex}
                IconSlot={iconLeading && <ItemIcon>{iconLeading}</ItemIcon>}
              />
            </ItemContent>
            {(showIndicator || isLoading) && indicator}
          </>
        )}
      </Item>
    </>
  );
};

export const ChoiceCard = memo(ChoiceCardImpl);

export default ChoiceCard;
